import { Intent } from "@blueprintjs/core";
import { ShowHide } from "components/layout";
import { NotificationInline } from "components/notifications";
import PropTypes from "prop-types";
import React, { useState } from "react";

import "./DocumentViewer.css";
import classNames from "classnames";
import { Button } from "components/buttons";
import './ImageCropper.css';
import './MediaList.css';
import './MediaPlaceholder.css';

export function DocumentViewer({
    url = null,
    mimeType,
    iframeHeight = "100%",
    limitMediaHeight = false,
    loading = false,
    downloadFile,
    isDownloading
}) {
    const imageKey = "image";
    const videoKey = "video";
    const iframeKey = "iframe";

    const allowedIframeTypes = ['pdf'];

    const allFileTypes = [imageKey, videoKey, iframeKey];
    const [currentFileTypeIndex, setCurrentFileTypeIndex] = useState(0);

    const noDocumentText = "No document provided";
    const brokenLinkText = "The link to the document is broken";
    const [loadError, setLoadError] = useState(false);
    const [fileTypeNotSupported, setFileTypeNotSupported] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    const onError = () => {
        if (currentFileTypeIndex < allFileTypes.length - 1) {
            const nextFileType = allFileTypes[currentFileTypeIndex + 1];
            if (nextFileType === iframeKey) {
                let fileType = "";

                if (url.startsWith("blob:")) {
                    fileType = mimeType?.split('/').pop() || "";
                } else {
                    fileType = url.split('?')[0].split('.').pop().toLowerCase();
                }

                if (!allowedIframeTypes.includes(fileType)) {
                    setFileTypeNotSupported(true);
                    setIsLoaded(true);
                    return;
                }
            }
            setCurrentFileTypeIndex(currentFileTypeIndex + 1);
        } else {
            setLoadError(true);
        }
    }

    const renderDocument = () => {

        if (loadError) {
            return (
                <NotificationInline intent={Intent.WARNING} text={brokenLinkText} />
            );
        }

        if (fileTypeNotSupported) {
            return <span className="document-viewer-unsupported-container">Cannot display a preview for the document. <Button large={false} intent={Intent.PRIMARY} disabled={isDownloading} onClick={() => downloadFile()} text={'Download'} /> the file instead.</span>
        }

        const currentFileType = allFileTypes[currentFileTypeIndex];
        switch (currentFileType) {
            case imageKey:
                return (
                    <img
                        alt="Document"
                        src={url}
                        loading="lazy"
                        onError={onError}
                        onLoad={() => setIsLoaded(true)}
                    />
                );
            case videoKey:
                return (
                    <video
                        src={url}
                        muted
                        controls
                        playsInline
                        preload="metadata"
                        onError={onError}
                        onLoadedData={() => setIsLoaded(true)}
                    />
                );
            default:
                return (
                    <iframe
                        title="Insurance Document"
                        src={url}
                        style={{ width: "100%", height: iframeHeight, border: "none" }}
                        allow="fullscreen"
                        onError={onError}
                        onLoad={() => setIsLoaded(true)}
                    />
                );
        }
    }

    const renderContent = () => {
        if (!loading) {
            if (url && url !== "") {
                return (
                    <div
                        className={classNames(
                            "document-viewer-container",
                            { 'limit-media-height': limitMediaHeight },
                            { 'bp6-skeleton': !isLoaded }
                        )}
                    >
                        {renderDocument()}
                    </div>
                );
            }
            return (
                <div
                    className={classNames(
                        "document-viewer-container",
                        { 'limit-media-height': limitMediaHeight }
                    )}
                >
                    <span className="document-viewer-unsupported-container">
                        The document cannot be displayed. File is missing or unavailable.
                    </span>
                </div>
            );
        }

        return <NotificationInline intent={Intent.WARNING} text={noDocumentText} />;
    };

    return (
        <>
            <ShowHide
                evaluator={loading}
                show={<div className="document-viewer-container bp6-skeleton" />}
            />
            <ShowHide evaluator={!loading} show={renderContent()} />
        </>
    );
}

DocumentViewer.propTypes = {
    url: PropTypes.string,
    iframeHeight: PropTypes.string,
    limitMediaHeight: PropTypes.bool,
    downloadFile: PropTypes.func.isRequired,
    isDownloading: PropTypes.bool.isRequired,
    mimeType: PropTypes.string,
    loading: PropTypes.bool
};
